import { useEffect, useRef, useState } from "react";
import { Box, Center, Image, Skeleton, useColorModeValue } from "@chakra-ui/react";
import { BBox } from "geojson";
import { CollecticonPicture } from "@devseed-ui/collecticons-chakra";

import API from "../api";

type Props = {
  sceneId: string;
  bbox?: BBox;
  width: number;
  height: number;
  brightness: number;
  contrast: number;
}

function SceneThumbnail({ sceneId, bbox, width, height, brightness, contrast }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const scale = window.devicePixelRatio;
  const [ image, setImage ] = useState<string>();
  const [ isLoading, setIsLoading ] = useState<boolean>(false);

  const cardThumbBgColor = useColorModeValue("gray.100", "base.600");
  const iconColor = useColorModeValue("primary.700", "gray.100");
  // Match Mapbox raster-brightness-min (b) + raster-contrast (c) using CSS filters.
  // CSS must be ordered contrast→brightness to avoid intermediate clamping that
  // would distort highlights. With this order, contrast never clips below the
  // Mapbox clip threshold, and brightness clips at exactly the same point.
  //   x (CSS brightness) = 1 + b*(1+c)
  //   y (CSS contrast)   = (1-b)*(1+c) / x
  const b = brightness / 10;
  const c = contrast / 10;
  const cssBrightness = 1 + b * (1 + c);
  const cssContrast = (1 - b) * (1 + c) / cssBrightness;
  useEffect(() => {
    // Lazy-load the thumbnail so we don't overwhelm the tiler
    if (!ref.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some(({ isIntersecting }) => isIntersecting)) return;
      setIsLoading(true);
      API.getScenePreview(sceneId, bbox?.join(), Math.floor(width * scale), Math.floor(height * scale), 1.25, 4)
        .then(r => {
          if (r.status < 400) {
            r.arrayBuffer().then(b => {
              const url = window.URL.createObjectURL(new Blob([b]));
              setImage(url);
            });
          }
        })
        .catch(() => setImage(undefined))
        .finally(() => setIsLoading(false));
    });

    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [bbox, height, scale, sceneId, width]);

  const h = `${height}px`;

  return (
    <Box height={h} bg={cardThumbBgColor} position="relative" ref={ref} overflow="hidden">
      <Skeleton isLoaded={!isLoading} height="100%">
        {image ? (
          <Image
            width="100%"
            height="100%"
            src={image}
            objectFit="cover"
            filter={`contrast(${cssContrast}) brightness(${cssBrightness})`}
          />
        ) : (
          <Center h="100%" position="relative" opacity="0.5">
            { bbox ? (
              <CollecticonPicture fontSize="8" color={iconColor} meaningful title="Scene image is still processing, or is unavailable" />
            ) : (
              <>
                <CollecticonPicture fontSize="8" color="gray.500" meaningful title="This scene does not have any images" />
                <Box
                  bgColor="gray.500"
                  height="4px"
                  width="48px"
                  position="absolute"
                  transform="rotate(-42deg)"
                />
              </>
            )}
          </Center>
        )}
      </Skeleton>
    </Box>
  );
}

export default SceneThumbnail;
